import { ActivityType } from "@/interfaces";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axios from "axios";

type QuickActivitySearchFilters = {
  countries: string[];
  city?: string;
  search: string;
  limit?: number;
};

export type InventoryPagination = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
};

export type InventoryPage<T> = { items: T[]; pagination: InventoryPagination };

export type ActivityInventoryFilters = {
  country?: string;
  city?: string;
  search?: string;
  category?: string;
  supplierId?: string;
  vehicleType?: string;
  minPax?: number | null;
  activeOnly?: boolean;
  excludeTransportCategories?: boolean;
  shareWithAgent?: boolean;
  inventoryOrigin?: string;
};

export type PaginatedActivityInventoryFilters = ActivityInventoryFilters & {
  page: number;
  limit: number;
};

const activityParams = (filters: ActivityInventoryFilters & Partial<PaginatedActivityInventoryFilters>, page?: number) => {
  const params = new URLSearchParams();
  Object.entries({ ...filters, page: page ?? filters.page, limit: filters.limit ?? 50 }).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  });
  return params;
};

/** Fetches one server-paginated activity inventory page for repository lists. */
export const usePaginatedActivitiesQuery = (filters: PaginatedActivityInventoryFilters, enabled = true) =>
  useQuery({
    queryKey: ["paginated-activities", filters],
    queryFn: async (): Promise<InventoryPage<ActivityType>> => {
      const response = await axios.get(`/api/activities?${activityParams(filters).toString()}`);
      return response.data;
    },
    enabled,
    staleTime: 1000 * 60 * 2,
  });

/**
 * Fetches a single activity by id.
 * @param id Activity id
 */
export const useGetActivity = (id?: string) => {
  return useQuery({
    queryKey: ["activity", id],
    queryFn: async (): Promise<ActivityType> => {
      const res = await axios.get(`/api/activities/${id}`);
      return res.data;
    },
    enabled: id ? true : false,
  });
};

/**
 * Fetches activities for inventory views filtered by country and city.
 * @param country Country filter
 * @param city City filter
 * @param enabled Whether the query should run
 */
export const useListActivitiesQuery = (country?: string, city?: string, enabled: boolean = true) => {
  const queryParams = activityParams({ country, city }).toString();
  return useQuery({
    queryKey: ["list-activities", country, city],
    queryFn: async (): Promise<InventoryPage<ActivityType>> => {
      const res = await axios.get(`/api/activities?${queryParams}`);
      return res.data;
    },
    select: (page) => page.items,
    enabled: enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/** Fetches activity inventory in capped pages for picker-style infinite lists. */
export const useInfiniteActivitiesQuery = (filters: ActivityInventoryFilters, enabled = true) => {
  const query = useInfiniteQuery({
    queryKey: ["infinite-activities", filters],
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<InventoryPage<ActivityType>> => {
      const response = await axios.get(`/api/activities?${activityParams(filters, pageParam as number).toString()}`);
      return response.data;
    },
    getNextPageParam: (lastPage) => (lastPage.pagination.hasNext ? lastPage.pagination.page + 1 : undefined),
    enabled,
    staleTime: 60_000,
  });
  return { ...query, items: query.data?.pages.flatMap((page) => page.items) || [] };
};

/**
 * Searches activity inventory by name across itinerary destination countries.
 * @param filters Country, city, search term, and limit for quick-add suggestions
 */
export const useQuickSearchActivitiesQuery = (filters: QuickActivitySearchFilters) => {
  const normalizedSearch = filters.search.trim();
  const countries = filters.countries.map((country) => country.trim()).filter(Boolean);
  return useQuery({
    queryKey: ["quick-search-activities", countries, filters.city, normalizedSearch, filters.limit],
    queryFn: async (): Promise<ActivityType[]> => {
      const params = new URLSearchParams();
      params.set("search", normalizedSearch);
      params.set("countries", countries.join(","));
      if (filters.city) params.set("city", filters.city);
      params.set("limit", String(filters.limit ?? 10));
      const res = await axios.get(`/api/activities?${params.toString()}`);
      return res.data.items;
    },
    enabled: normalizedSearch.length > 0 && countries.length > 0,
    staleTime: 1000 * 60,
  });
};
